import React, { useCallback, useEffect, useState } from "react";
import { Copyright, Home } from "@mui/icons-material";
import SubmittedCommon from "../SubmittedCommon";
import IconBreadcrumbs from "../custom/IconBreadcrumbs";
import debounce from "lodash/debounce";
import ReusableTable from "../custom/data_table/ReusableTable";
import {
  useCertificateIssueEndorseMutation,
  useGetCertificateListQuery,
  useGetEndorseListQuery,
} from "../../redux/api/submittedCopyrightAgreementApiSlice";
import { localDateFormat } from "../../utils/dateFormat";
import { Link } from "react-router-dom";
import CountCategoryCopyright from "../count_category/CountCategoryCopyright";
import { accessRoles } from "../../utils/accessRoles";
import { Form } from "react-bootstrap";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { useAuth } from "../../utils/authorization";

const CertificateFile = ({
  fileStatus,
  applicationPath,
  detailsRoute,
  pageTitle,
}) => {
  // State to track issue status for each certificate
  const [issueStatus, setIssueStatus] = useState({});
  const [date, setDate] = useState("");
  const [category_id, setCategoryId] = useState("");
  const [catId, setCatId] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const { data, isLoading, refetch } = useGetCertificateListQuery({
    date,
    trackReg: searchValue,
    category_id: category_id || catId,
    page,
    fileStatus,
  });
  const { authUser } = useAuth();

  // certificate endorse issue
  const [issue, { isSuccess: isSuccessIssue, data: dataIssue }] =
    useCertificateIssueEndorseMutation();

  /// copyright application length
  const { data: copyright } = useGetCertificateListQuery({
    date: "",
    trackReg: "",
    category_id: "",
    subCategory: "",
    page: "",
    fileStatus,
  });
  /// agreement application length
  const { data: agreement } = useGetEndorseListQuery({
    date: "",
    trackReg: "",
    category_id: "",
    subCategory: "",
    page: "",
    fileStatus,
  });

  const total = {
    copyright: copyright?.meta?.total ?? 0,
    agreement: agreement?.meta?.total ?? 0,
  };

  const breadcrumbsData = [
    { icon: Home, title: "হোম", url: "/" },
    {
      icon: Copyright,
      title: pageTitle ? "কপিরাইট - " + pageTitle : "কপিরাইট আবেদন পত্র",
      active: true,
    },
  ];

  // data table start
  const totalData = data?.meta?.total;
  const lastPage = data?.meta?.last_page;

  const debouncedSearch = useCallback(
    debounce((value) => {
      setPage(1);
      setSearchValue(value);
      refetch();
    }, 400),
    []
  );

  useEffect(() => {
    // Cleanup function to cancel the debounce when the component unmounts or debouncedSearch changes
    return () => {
      debouncedSearch?.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (value) => {
    setSearchText(value);
    debouncedSearch(value);
  };

  const handlePageChange = (page) => {
    setPage(page);
    refetch();
  };

  // handleIssue
  const handleIssue = (rowData) => {
    const { id, sendUserId } = rowData;

    Swal.fire({
      text: "আপনি কি নিশ্চিত?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "না",
      confirmButtonText: "হ্যাঁ",
    }).then((result) => {
      if (result.isConfirmed) {
        issue({ id, formData: { user_id: sendUserId } }).then(() => {
          setIssueStatus((prev) => ({ ...prev, [id]: !prev[id] })); // Toggle the issue status
          refetch();
        });
      }
    });
  };

  useEffect(() => {
    if (isSuccessIssue) {
      toast.success(
        dataIssue?.message || "Certificate has been successfully issued."
      );
    }
  }, [dataIssue]);

  const columns = [
    { label: "তারিখ", name: "sendDate" },
    {
      label: "আবেদন নং",
      name: "trackReg",
      options: {
        customBodyRender: (value, tableMeta) => {
          const id = tableMeta.rowData.id;
          return (
            <Link
              className="text-primary"
              to={`${detailsRoute}/${id}/${tableMeta.rowData?.sendUserId}`}
            >
              {value}
            </Link>
          );
        },
      },
    },
    { label: "আবেদনকারির নাম", name: "applicant" },
    {
      label: "শিরোনাম",
      name: "workTitle",
      options: {
        customBodyRender: (value, tableMeta) => {
          const id = tableMeta.rowData.id;
          return (
            <Link
              className="text-primary"
              to={`${detailsRoute}/${id}/${tableMeta.rowData?.sendUserId}`}
            >
              {value}
            </Link>
          );
        },
      },
    },

    { label: "বিভাগ", name: "category" },
    { label: "উপবিভাগ", name: "subCategory" },
  ];

  if (authUser?.role_id === accessRoles.Register) {
    columns.push({
      label: "রেজিস্টার কর্তৃক সনদ প্রদান",
      name: "id",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowId = tableMeta.rowData.id; // Use rowId to access the correct item
          return (
            <div className="">
              <Form.Check
                type="switch"
                id={`custom-switch-${rowId}`}
                checked={issueStatus[rowId] || false} // Set the checked state based on issueStatus
                label={"সার্টিফিকেট ইস্যু"}
                onChange={() => handleIssue(tableMeta.rowData)}
              />
            </div>
          );
        },
      },
    });
  }

  const dataTable = data?.data.map((item, i) => ({
    sendDate: localDateFormat(item?.sendDate),
    id: item?.id,
    trackReg: item?.trackReg,
    applicant: item?.applicantName,
    sendUserId: item?.sendUserId,
    workTitle: item?.profileDetail?.workTitle,
    category: item?.profileDetail?.category?.name,
    subCategory: item?.profileDetail?.subCategory?.name,
  }));

  // if (isLoading) {
  //   return <Loader />;
  // }

  return (
    <div>
      <div className="py-3">
        <IconBreadcrumbs breadcrumbs={breadcrumbsData} />
      </div>
      <div className="slideUp dashboard-layout p-3">
        <div>
          <SubmittedCommon
            total={total}
            applicationPath={applicationPath}
            date={date}
            setDate={setDate}
            category_id={category_id}
            setCategoryId={setCategoryId}
            subCategory={subCategory}
            setSubCategory={setSubCategory}
          />
        </div>
        <CountCategoryCopyright
          setCategoryId={setCatId}
          category_id={catId}
          fileStatus={fileStatus}
        />
        <div>
          <ReusableTable
            title={"কপিরাইট আবেদন"}
            columns={columns}
            data={dataTable}
            isLoading={isLoading}
            currentPage={page}
            lastPage={lastPage}
            totalData={totalData}
            onSearchChange={handleSearchChange}
            searchText={searchText}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default CertificateFile;
